import {
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  LineChart,
  Target,
  UtensilsCrossed,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router';
import { useGetMe } from '@/api/me/me';
import { cn } from '@/lib/utils';

const TABS = [
  { to: '/', label: 'วันนี้', icon: UtensilsCrossed },
  { to: '/plan', label: 'แผนมื้อ', icon: ClipboardList },
  { to: '/history', label: 'ประวัติ', icon: CalendarDays },
  { to: '/trends', label: 'แนวโน้ม', icon: LineChart },
  { to: '/goal', label: 'เป้าหมาย', icon: Target },
] as const;

/** Only admins can act on the review queue, so only they get the tab. */
const ADMIN_TAB = { to: '/admin', label: 'ตรวจอาหาร', icon: ClipboardCheck } as const;

/** Tailwind only ships classes it can see written out, so spell each one. */
const GRID_COLS: Record<number, string> = {
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};

/**
 * Phone-shaped shell with a bottom tab bar — a LIFF view opens full-screen
 * inside the LINE app, where a top nav would collide with LINE's own header.
 * The max-width keeps it sane when the same URL is opened on a desktop.
 */
export function Layout() {
  const me = useGetMe();
  const tabs = me.data?.isAdmin ? [...TABS, ADMIN_TAB] : TABS;

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col">
      <main className="flex-1 space-y-4 p-4 pb-24">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 mx-auto max-w-md border-t border-border-subtle bg-surface pb-[env(safe-area-inset-bottom)]">
        <ul className={cn('grid', GRID_COLS[tabs.length])}>
          {tabs.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex flex-col items-center gap-1 px-1 py-2.5 transition-colors',
                    isActive ? 'text-brand' : 'text-ink-muted',
                  )
                }
              >
                <Icon className="size-5 shrink-0" aria-hidden />
                {/* Six Thai labels across a phone: one line each, or the bar wraps. */}
                <span className="w-full truncate text-center text-[11px] leading-tight">
                  {label}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/** Spec §12 requires this on the dashboard and the goal screen. */
export function MedicalDisclaimer() {
  return (
    <p className="px-1 text-center text-caption text-ink-muted">
      เครื่องมือนี้ประมาณแคลอรีเพื่อสุขภาพทั่วไป ไม่ใช่คำแนะนำทางการแพทย์
    </p>
  );
}
